import { useEffect, useState } from "react";
import { Button } from "./ui/Button";
import { Switch } from "./ui/Switch";

const STORAGE_KEY = "habily_cookie_preferences_v1";

const defaultPrefs = () => ({
  necessary: true,
  preferences: false,
  analytics: false,
  marketing: false,
});

function loadPrefs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultPrefs();
    const parsed = JSON.parse(raw);
    return {
      necessary: true,
      preferences: Boolean(parsed.preferences),
      analytics: Boolean(parsed.analytics),
      marketing: Boolean(parsed.marketing),
    };
  } catch {
    return defaultPrefs();
  }
}

function savePrefs(prefs) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        preferences: prefs.preferences,
        analytics: prefs.analytics,
        marketing: prefs.marketing,
        updatedAt: new Date().toISOString(),
      })
    );
  } catch {
    /* ignore */
  }
}

const Row = ({ title, description, checked, onCheckedChange, disabled, id }) => (
  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 py-4 border-b border-border/40 last:border-0">
    <div className="space-y-1 pr-0 sm:pr-6">
      <label htmlFor={id} className="text-base font-semibold text-foreground cursor-pointer block">
        {title}
      </label>
      <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
    </div>
    <div className="flex items-center gap-3 shrink-0">
      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide w-10 text-right sm:text-left">
        {disabled ? "On" : checked ? "On" : "Off"}
      </span>
      <Switch
        id={id}
        checked={checked}
        onCheckedChange={disabled ? undefined : onCheckedChange}
        disabled={disabled}
      />
    </div>
  </div>
);

/**
 * Panel interactivo de preferencias de cookies (política / segunda capa).
 * Persistencia en localStorage; las cookies técnicas siguen activas siempre.
 */
export const CookiePreferencesPanel = () => {
  const [prefs, setPrefs] = useState(defaultPrefs);
  const [savedMsg, setSavedMsg] = useState("");

  useEffect(() => {
    setPrefs(loadPrefs());
  }, []);

  const showSaved = () => {
    setSavedMsg("Preferencias guardadas.");
    window.setTimeout(() => setSavedMsg(""), 4000);
  };

  const acceptAll = () => {
    const next = { necessary: true, preferences: true, analytics: true, marketing: true };
    setPrefs(next);
    savePrefs(next);
    showSaved();
  };

  const rejectNonEssential = () => {
    const next = { necessary: true, preferences: false, analytics: false, marketing: false };
    setPrefs(next);
    savePrefs(next);
    showSaved();
  };

  const saveConfiguration = () => {
    savePrefs(prefs);
    showSaved();
  };

  return (
    <div
      id="panel-configuracion-cookies"
      className="rounded-2xl border border-primary/25 bg-card/80 backdrop-blur-md shadow-lg overflow-hidden scroll-mt-28"
    >
      <div className="px-6 py-5 border-b border-border/40 bg-gradient-to-r from-primary/10 to-accent/10">
        <h2 className="text-lg font-bold text-foreground">Panel de configuración de cookies</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Activa o desactiva cada categoría. Las cookies necesarias no se pueden desactivar: la web no funcionaría sin
          ellas.
        </p>
      </div>

      <div className="px-6 py-2">
        <Row
          id="cookie-necesarias"
          title="Cookies necesarias (técnicas)"
          description="Navegación segura, sesión, seguridad y carga básica del sitio. Siempre activas."
          checked
          disabled
        />
        <Row
          id="cookie-preferencias"
          title="Cookies de preferencias"
          description="Recuerdan idioma, región u otras opciones de interfaz que eliges."
          checked={prefs.preferences}
          onCheckedChange={(v) => setPrefs((p) => ({ ...p, preferences: Boolean(v) }))}
        />
        <Row
          id="cookie-analiticas"
          title="Cookies de análisis o medición"
          description="Nos ayudan a entender cómo se usa la web de forma agregada y anónima."
          checked={prefs.analytics}
          onCheckedChange={(v) => setPrefs((p) => ({ ...p, analytics: Boolean(v) }))}
        />
        <Row
          id="cookie-marketing"
          title="Cookies de marketing"
          description="Permiten mostrar contenidos o anuncios más relevantes según tu navegación."
          checked={prefs.marketing}
          onCheckedChange={(v) => setPrefs((p) => ({ ...p, marketing: Boolean(v) }))}
        />
      </div>

      <div className="px-6 py-5 bg-secondary/30 border-t border-border/40 flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row flex-wrap gap-3">
          <Button type="button" variant="hero" size="sm" onClick={acceptAll}>
            Aceptar todas
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={rejectNonEssential}>
            Rechazar las no esenciales
          </Button>
          <Button type="button" variant="secondary" size="sm" onClick={saveConfiguration}>
            Guardar configuración
          </Button>
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed">
          Si pulsas <strong className="text-foreground">Guardar configuración</strong> sin haber activado ninguna
          opción opcional, se guardará la selección actual (equivalente a no consentir cookies de preferencias,
          análisis ni marketing). Puedes cambiar tu decisión en cualquier momento desde esta página.
        </p>
        {savedMsg ? (
          <p className="text-sm font-medium text-primary" role="status">
            {savedMsg}
          </p>
        ) : null}
      </div>
    </div>
  );
};
